import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import type {
  ReceivedDocumentListItem,
  ListReceivedDocumentsResponse,
} from "../types";

export interface ReceivedDocumentsFilters {
  status?: "pending" | "accepted" | "rejected";
  documentTypeCode?: string;
  issuerTaxId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export interface UseReceivedDocumentsOptions {
  filters?: ReceivedDocumentsFilters;
  limit?: number;
  offset?: number;
}

export interface UseReceivedDocumentsResult {
  documents: ReceivedDocumentListItem[];
  total: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useReceivedDocuments(
  options: UseReceivedDocumentsOptions = {}
): UseReceivedDocumentsResult {
  const { filters = {}, limit = 50, offset = 0 } = options;

  const [documents, setDocuments] = useState<ReceivedDocumentListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchDocuments = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.documentTypeCode) params.append("documentTypeCode", filters.documentTypeCode);
      if (filters.issuerTaxId) params.append("issuerTaxId", filters.issuerTaxId);
      if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.append("dateTo", filters.dateTo);
      if (filters.search) params.append("search", filters.search);
      params.append("limit", String(limit));
      params.append("offset", String(offset));

      const queryString = params.toString();
      const url = `/api/received-documents${queryString ? `?${queryString}` : ""}`;

      const response = await apiFetch.get<ListReceivedDocumentsResponse>(url);
      setDocuments(response.items);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch documents"));
    } finally {
      setIsLoading(false);
    }
  }, [
    filters.status,
    filters.documentTypeCode,
    filters.issuerTaxId,
    filters.dateFrom,
    filters.dateTo,
    filters.search,
    limit,
    offset,
  ]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  return {
    documents,
    total,
    isLoading,
    error,
    refetch: fetchDocuments,
  };
}
